import React from 'react';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';

import ImageWithFallback from 'components/ImageWithFallback';
import useViewport from 'hooks/useViewports';
import { getFilename, isImageName } from 'utils/images-ts';
import { Tooltip } from '@material-ui/core';
import TruncateText from 'components/TruncateText';

const styles = (theme: Theme) =>
  createStyles({
    list: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      overflow: 'auto',
      gap: 32,
    },
    listColumn: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      overflow: 'auto',
      gap: 32,
    },
    downloadText: {
      fontSize: 13,
      fontWeight: 'bolder',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    document: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: 8,
      overflow: 'hidden',
    },
    buttonContainer: {
      padding: 20,
    },
  });

interface HorizontalImageListProps extends WithStyles<typeof styles> {
  wrap?: boolean;
  imageUrls: string[];
  height: string | number;
  withActionButtons?: boolean;
  imageResize?: (url: string) => string;
  onClickActionButton?: (src: string) => void;
  onClickImage?: (index: number) => void;
  onClickDocument?: (url: string) => void;
}

const HorizontalImageList = ({
  wrap,
  classes,
  imageUrls,
  height,
  withActionButtons,
  imageResize,
  onClickActionButton,
  onClickImage,
  onClickDocument,
}: HorizontalImageListProps) => {
  const { mdUp } = useViewport();

  let imageStyles: React.CSSProperties = { maxHeight: height, height: '100%', maxWidth: '100%' };
  if (mdUp) {
    imageStyles = { height: height };
  }

  return (
    <div className={mdUp ? classes.list : classes.listColumn} style={{ flexWrap: wrap ? 'wrap' : undefined }}>
      {imageUrls.map((i, index) => {
        const name = getFilename(i) || '';
        const isPDF = name.endsWith('.pdf');
        const isImage = isImageName(name);
        const documentStyle: React.CSSProperties = {
          height,
          width: typeof height === 'number' ? height / 1.414 : `calc(${height} / 1.414)`,
          cursor: onClickDocument ? 'pointer' : undefined,
        }

        if (!isImage && !isPDF) {
          const dotIndex = name.lastIndexOf('.');
          const fileName = dotIndex > 0 ? name.slice(0, dotIndex) : name;
          const extension = dotIndex > 0 ? name.slice(dotIndex + 1).toLowerCase() : '';

          return (
            <Tooltip key={i} title={name} placement='top'>
              <div className={classes.container}>
                <div
                  className={classes.document}
                  style={{ ...documentStyle, padding: 16 }}
                  onClick={() => window.open(i, '_blank')}
                >
                  <div style={{ fontWeight: 700, fontSize: 24 }} >{extension?.toUpperCase()}</div>
                  <TruncateText
                    disableTooltip
                    title={fileName}
                    fixedAfter={extension ? `.${extension}` : undefined}
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              </div>
            </Tooltip>
          );
        }

        const imageUrl = imageResize ? imageResize(i) : i;

        return (
          // @ts-ignore: ImageWithFallback is a JS component
          <ImageWithFallback key={i} src={imageUrl}>
            {(src: string) => {
              return (
                <Tooltip title={name} placement='top'>
                  <div className={classes.container}>
                    <img
                      className={isPDF ? classes.document : undefined}
                      style={{
                        ...imageStyles,
                        ...(isPDF ? { ...documentStyle } : {}),
                        cursor: (isPDF || onClickImage) ? 'pointer' : undefined,
                      }}
                      src={src}
                      alt=""
                      onClick={() => {
                        if (isPDF) {
                          window.open(i, '_blank');
                        } else {
                          onClickImage?.(index);
                        }
                      }}
                    />
                    {withActionButtons && onClickActionButton && (
                      <div className={classes.buttonContainer}>
                        <Button
                          className="ProductPreview-DownloadImage"
                          variant="outlined"
                          color="primary"
                          onClick={() => onClickActionButton(src)}
                        >
                          <span className={classes.downloadText}>Ladda ner</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </Tooltip>
              );
            }}
          </ImageWithFallback>
        );
      })}
    </div>
  );
};

export default withStyles(styles)(HorizontalImageList);
